import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { listScheduleEvents } from '@sportninja/common/services/event'

interface UseScheduleHasEventsArgs {
  scheduleId?: string | null
  /**
   * Skips the query when the answer cannot change the outcome — the caller
   * already knows the competition has games, so it is showing the list either
   * way and does not need to pay for this fetch.
   */
  enabled?: boolean
}

interface UseScheduleHasEventsReturn {
  /** True once the list has come back holding at least one event. */
  hasEvents: boolean
  /** True while the answer is still unknown. */
  isLoading: boolean
}

/**
 * Whether a competition holds any events at all.
 *
 * The Schedule tab decides between the setup wizard and the real list from
 * `games_count`, which the schedule item carries. That field counts GAMES
 * only — there is no `events_count` on the API today — so a competition whose
 * entries are all events looked empty and was shown the wizard, with the list
 * (and therefore the events fetch) never mounting at all.
 *
 * This asks the events endpoint directly rather than waiting for a count field
 * to exist. It is deliberately a separate, narrowly-scoped query: the gate has
 * to answer before the list mounts, so it cannot read the list's own data.
 *
 * Cheap in the common case — it shares `queryKeys.events.bySchedule` with the
 * schedule list, so once that list has loaded this resolves from cache rather
 * than refetching.
 */
export const useScheduleHasEvents = ({
  scheduleId,
  enabled = true,
}: UseScheduleHasEventsArgs): UseScheduleHasEventsReturn => {
  'use memo'

  const query = useQuery({
    queryKey: queryKeys.events.bySchedule(scheduleId, null),
    queryFn: () => listScheduleEvents(scheduleId as string),
    enabled: enabled && Boolean(scheduleId),
  })

  return {
    hasEvents: (query.data?.length ?? 0) > 0,
    isLoading: query.isLoading,
  }
}
